import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface RetryFailedRelativityUploadRequest {
  matterId: string;
  uploadId: string;
  uploadFailureIds: string[];
}

interface RetryFailedRelativityUploadResponse {
  jobId: string;
  uploadId: string;
  retriedCount: number;
  skippedCount: number;
}

export const useRetryFailedRelativityUpload = () => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation<RetryFailedRelativityUploadResponse, Error, RetryFailedRelativityUploadRequest>(
    async ({ uploadId, uploadFailureIds }) => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: { uploadFailureIds },
      });

      const response = await fetch(`${APIBaseChronos}/api/relativity/uploads/${uploadId}/retry-failed`, fetchConfig);

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to retry selected documents');
      }

      return response.json();
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([QueryEntity.UploadHistory, { matterId: variables.matterId }]);
        queryClient.invalidateQueries([
          QueryEntity.UploadHistory,
          { matterId: variables.matterId, uploadId: variables.uploadId },
        ]);
        queryClient.invalidateQueries([QueryEntity.FileHistoryStats, { matterId: variables.matterId }]);
        queryClient.invalidateQueries([QueryEntity.Files, { matterId: variables.matterId }]);
        toast.success(`Retry started for ${data.retriedCount} document${data.retriedCount === 1 ? '' : 's'}`);
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to retry selected documents');
      },
    },
  );
};
